import { useCallback, useSyncExternalStore } from 'react';
import { useSelector } from 'react-redux';
import { customerIdSelector } from 'packages/store/user/user.selectors';
import { AssetType, UploaderState, UploadService } from './types';
import { uploadAsset } from './uploadAsset';

const DEFAULT_STATE: UploaderState = {
    status: 'ready',
    uploadingCount: 0,
    totalCount: 0,
};

let globalState: UploaderState = DEFAULT_STATE;
let abortControllers: AbortController[] = [];
const subscriptions = new Set<() => void>();

const subscribe = (callback: () => void) => {
    subscriptions.add(callback);

    return () => subscriptions.delete(callback);
};

const notifySubscribers = () => {
    subscriptions.forEach((callback) => callback());
};

const uploadFiles = async (
    files: File[] | File,
    customerId: number,
    assetTypes: AssetType[],
    collectionId?: number,
    uploadService?: UploadService,
    hideNotification?: boolean,
) => {
    const fileArray = Array.isArray(files) ? files : [files];
    const currentUploadService = uploadService || uploadAsset;
    abortControllers = [];

    globalState = {
        ...globalState,
        totalCount: fileArray.length,
    };

    for (const [i, element] of fileArray.entries()) {
        const controller = new AbortController();
        abortControllers.push(controller);

        globalState = {
            ...globalState,
            uploadingCount: i + 1,
        };

        for await (const partialState of currentUploadService({
            file: element,
            customerId,
            abortSignal: controller.signal,
            assetTypes,
            collectionId,
            hideNotification,
        })) {
            globalState = { ...globalState, ...partialState };
            notifySubscribers();
        }
    }

    // Reset after all uploads complete
    globalState = DEFAULT_STATE;
    notifySubscribers();
    abortControllers = [];
};

const cancel = () => {
    abortControllers.forEach((controller) => controller.abort());
    abortControllers = [];
    globalState = { ...DEFAULT_STATE };
};

const useUploader = <T>({ selector }: { selector: (state: UploaderState) => T }) => {
    const customerId = useSelector(customerIdSelector);
    const state = useSyncExternalStore(subscribe, () => selector(globalState));

    const upload = useCallback(
        (
            files: File[] | File,
            assetTypes: AssetType[],
            collectionId?: number,
            uploadService?: UploadService,
            hideNotification?: boolean,
        ) => {
            uploadFiles(files, customerId, assetTypes, collectionId, uploadService, hideNotification);
        },
        [customerId],
    );

    return {
        state,
        upload,
        cancel,
    };
};

export default useUploader;
